import type { ReadyResponse } from '@ofmos/shared/health';
import type { InstanceInfoResponse } from '@ofmos/shared/instance';
import type {
  AdminClient,
  AgentObjectPolicy,
  AgentPolicySnapshot,
  MigrationActionSummary,
} from '../client';
import type { Transport } from './transport';

/**
 * `AdminClient` over the operator routes: `/api/agent-policy`, `/api/instance`,
 * `/api/metadata/actions` and `/ready`.
 *
 * The wire shapes here already match what the screens render, so this file is thin on
 * purpose - there is no second vocabulary for "is the agent allowed to write", and the
 * one place that decides is the server (`routes/agent-policy.ts` computes
 * `effectiveDirectWrite`, never the browser).
 */
export class HttpAdminClient implements AdminClient {
  constructor(private readonly transport: Transport) {}

  getAgentPolicy(): Promise<AgentPolicySnapshot> {
    return this.transport.request<AgentPolicySnapshot>({
      method: 'GET',
      path: '/api/agent-policy',
    });
  }

  setGlobalAgentPolicy(changes: {
    readonly agentWritesForcePending?: boolean;
    readonly agentAnnotationsEnabled?: boolean;
  }): Promise<AgentPolicySnapshot> {
    return this.transport.request<AgentPolicySnapshot>({
      method: 'PUT',
      path: '/api/agent-policy',
      body: changes,
    });
  }

  setObjectAgentPolicy(objectId: string, directWrite: boolean): Promise<AgentObjectPolicy> {
    return this.transport.request<AgentObjectPolicy>({
      method: 'PUT',
      path: `/api/agent-policy/${objectId}`,
      body: { directWrite },
    });
  }

  getInstance(): Promise<InstanceInfoResponse> {
    return this.transport.request<InstanceInfoResponse>({ method: 'GET', path: '/api/instance' });
  }

  async listMigrationActions(): Promise<readonly MigrationActionSummary[]> {
    const response = await this.transport.request<{
      actions: readonly MigrationActionSummary[];
    }>({ method: 'GET', path: '/api/metadata/actions' });
    return response.actions;
  }

  async retryMigrationAction(actionId: string): Promise<void> {
    await this.transport.request<unknown>({
      method: 'POST',
      path: `/api/metadata/actions/${actionId}/retry`,
    });
  }

  /**
   * `/ready` is the OPEN probe, and the instance panel reads it rather than a new
   * authenticated endpoint (D24): it already returns database up/down plus the webhook
   * outbox depth, dead count and the age of the oldest undispatched row - the four
   * numbers that distinguish "worker down" from "dispatcher wedged" from "quiet
   * afternoon", and nothing about them is configuration worth hiding.
   */
  getReady(): Promise<ReadyResponse> {
    return this.transport.request<ReadyResponse>({ method: 'GET', path: '/ready' });
  }
}
